'use client';

import { platform } from '@todesktop/client-core';
import { Loader2 } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';

import type { AuthMode } from '../types';

import { GithubIcon } from '@/components/icons';
import { useToDesktop } from '@/components/providers/todesktop-auth-provider';
import { Button } from '@/components/ui/button';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import {
  buildGithubAuthRedirectPath,
  buildGithubAuthRedirectUrl,
} from '@/lib/auth/github-auth-redirect';
import { AUTH_FLOW, AUTH_MODE, REFERRAL_QUERY_PARAMS } from '@/lib/constants';

type GitHubAuthButtonProps = {
  mode: AuthMode;
  inviteCode?: string;
  returnTo?: string;
};

function buildRetryToFromLocation(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const { pathname, search, hash } = window.location;
  if (!pathname.startsWith('/')) {
    return undefined;
  }

  return `${pathname}${search}${hash}`;
}

export function GitHubAuthButton({ mode, inviteCode, returnTo }: GitHubAuthButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { isDesktopApp } = useToDesktop();
  const searchParams = useSearchParams();

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      const referralCode = inviteCode || searchParams.get(REFERRAL_QUERY_PARAMS.INVITE_CODE);

      // Check if running in ToDesktop app using the context
      if (isDesktopApp) {
        const authRedirectUrl = buildGithubAuthRedirectUrl({
          flowType: AUTH_FLOW.DESKTOP,
          mode,
          inviteCode: referralCode,
          returnTo,
        });
        await platform.os.openURL(authRedirectUrl);

        // For desktop app, we reset loading state after opening browser
        // since we won't get redirect back to this component
        setIsLoading(false);
      } else {
        trackClientEvent('clicked_github_login_button_with_invite_code', undefined, {
          invite_code: referralCode,
        });
        const retryTo = buildRetryToFromLocation();
        // Web auth navigates to the route first so OAuth URL generation stays server-side.
        // The route is responsible for either redirecting to GitHub or bouncing back with
        // a query-backed error state if auth startup fails.
        const authRedirectPath = buildGithubAuthRedirectPath({
          flowType: AUTH_FLOW.WEB,
          mode,
          inviteCode: referralCode,
          returnTo,
          retryTo,
        });
        window.location.assign(authRedirectPath);
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Failed to login:', error);
    }
  };

  return (
    <Button
      variant="default"
      className="w-full min-w-[220px] [&_svg]:opacity-100 hover:[&_svg]:opacity-90"
      onClick={handleLogin}
      disabled={isLoading}
      aria-busy={isLoading}
    >
      <span className="relative size-4">
        <span
          className={`absolute inset-0 transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        >
          <GithubIcon className="size-4" />
        </span>
        <span
          className={`absolute inset-0 transition-opacity duration-300 ${isLoading ? 'opacity-100' : 'opacity-0'}`}
        >
          <Loader2 className="size-4 animate-spin" />
        </span>
      </span>
      {isLoading
        ? mode === AUTH_MODE.SIGN_IN
          ? 'Signing in...'
          : 'Signing up...'
        : mode === AUTH_MODE.SIGN_IN
          ? 'Sign in with GitHub'
          : 'Sign up with GitHub'}
    </Button>
  );
}
